import { 
  AnyWorkflowInstance, 
  MonthlySupervisionStep, 
  SupervisionFormStep, 
  DischargeStep, 
  SupervisionFormData,
  WorkflowType 
} from '../types/workflow';

// Validation error interface to match StepContainer
export interface ValidationError {
  field: string;
  message: string;
  severity?: 'error' | 'warning' | 'info';
}

// Validation result interface
export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
  fieldErrors: Record<string, string>;
}

// Field validation rule interface
export interface FieldValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  format?: 'email' | 'phone' | 'date' | 'time' | 'url' | 'number';
  min?: number;
  max?: number;
  customValidator?: (value: any, allData?: any) => string | null;
  dependencies?: string[]; // Fields this validation depends on
}

// Validation rules for different field types
const VALIDATION_PATTERNS = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  phone: /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/,
  date: /^\d{4}-\d{2}-\d{2}$/,
  time: /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/,
  url: /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/,
  number: /^-?\d*\.?\d+$/
};

// Field validation rules for supervision form
const SUPERVISION_FORM_VALIDATION_RULES: Record<string, FieldValidationRule> = {
  // Basic Information
  supervisionDate: {
    required: true,
    format: 'date',
    customValidator: (value) => {
      const date = new Date(value);
      const now = new Date();
      if (date > now) return 'Supervision date cannot be in the future';
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(now.getMonth() - 6);
      if (date < sixMonthsAgo) return 'Supervision date cannot be more than 6 months ago';
      return null;
    }
  },
  supervisionType: {
    required: true,
    customValidator: (value) => {
      if (!['PATIENT', 'AIDE', 'BOTH'].includes(value)) {
        return 'Must select a valid supervision type';
      }
      return null;
    }
  },
  employeeId: {
    required: true,
    customValidator: (value) => {
      if (!value || value.trim() === '') return 'Employee selection is required';
      return null;
    }
  },
  patientId: {
    required: false,
    dependencies: ['supervisionType'],
    customValidator: (value, allData) => {
      if (allData?.supervisionType !== 'AIDE' && (!value || value.trim() === '')) {
        return 'Patient selection is required for patient supervision';
      }
      return null;
    }
  },

  // Header Information
  aideName: {
    required: true,
    minLength: 2,
    maxLength: 100,
    customValidator: (value) => {
      if (!value || value.trim() === '') return 'Aide name is required';
      if (!/^[a-zA-Z\s'-]+$/.test(value)) return 'Aide name can only contain letters, spaces, hyphens, and apostrophes';
      return null;
    }
  },
  clientNdocNumber: {
    required: false,
    maxLength: 50,
    pattern: /^[A-Z0-9-]+$/,
    customValidator: (value) => {
      if (value && value.length > 0 && !/^[A-Z0-9-]+$/.test(value)) {
        return 'Patient number must contain only uppercase letters, numbers, and hyphens';
      }
      return null;
    }
  },

  // Section I: Patient and Staff Identification (6 fields)
  aideAppropratelyAttired: { required: true },
  introducesSlefToPatient: { required: true },
  clearlyIdentifiesPatient: { required: true },
  reliableAndPunctual: { required: true },
  goodRelationshipWithPatient: { required: true },
  ensuresPatientRights: { required: true },

  // Section II: Infection Control (2 fields)
  usesStandardPrecautions: { required: true },
  followsSpecificPrecautions: { required: true },

  // Section III: Treatment/Tasks (11 fields + special tasks)
  followsCarePlan: { required: true },
  personalCareSkinBreakdown: { required: true },
  dressing: { required: true },
  mealPreparationFollowsDiet: { required: true },
  transfers: { required: true },
  exercises: { required: true },
  catheterCare: { required: true },
  ostomyCare: { required: true },
  vitalSignsWeight: { required: true },
  medicationReminding: { required: true },
  lightHousekeeping: { required: true },
  errandsShoppig: { required: true }, // Note: keeping original field name with typo for compatibility
  specialTasks: {
    required: false,
    maxLength: 500,
    customValidator: (value) => {
      if (value && value.length > 500) return 'Special tasks description cannot exceed 500 characters';
      return null;
    }
  },

  // Section IV: Communication (4 fields)
  documentationNoAbbreviations: { required: true },
  contactsCMChangesInCondition: { required: true },
  contactsCMPatientComplaints: { required: true },
  contactsCMPatientSafety: { required: true },

  // Section V: Medications (1 field + comments)
  medicationNonCompliance: { required: true },
  commentsRecommendationsPage1: {
    required: false,
    maxLength: 1000,
    customValidator: (value) => {
      if (value && value.length > 1000) return 'Comments cannot exceed 1000 characters';
      return null;
    }
  },

  // Page 2 - Social (4 fields + comments)
  involvesPatientInPlanning: { required: true },
  allowsPatientControl: { required: true },
  recognizesHearingSightLoss: { required: true },
  slowsPaceForElderly: { required: true },
  commentsRecommendationsPage2: {
    required: false,
    maxLength: 1000,
    customValidator: (value) => {
      if (value && value.length > 1000) return 'Comments cannot exceed 1000 characters';
      return null;
    }
  },

  // Validation Section (2 fields)
  additionalValidationRequired: {
    required: true,
    customValidator: (value) => {
      if (typeof value !== 'boolean') return 'Additional validation required must be selected';
      return null;
    }
  },
  performanceAchieved: {
    required: true,
    customValidator: (value) => {
      if (typeof value !== 'boolean') return 'Performance achieved must be selected';
      return null;
    }
  },

  // Digital Signatures (3 fields)
  employeeSignature: {
    required: true,
    customValidator: (value) => {
      if (!value || value.trim() === '') return 'Employee signature is required';
      if (value.length < 10) return 'Employee signature appears incomplete';
      return null;
    }
  },
  evaluatorSignature: {
    required: true,
    customValidator: (value) => {
      if (!value || value.trim() === '') return 'Evaluator signature is required';
      if (value.length < 10) return 'Evaluator signature appears incomplete';
      return null;
    }
  },
  patientSignature: {
    required: false,
    dependencies: ['supervisionType'],
    customValidator: (value, allData) => {
      if (allData?.supervisionType === 'PATIENT' || allData?.supervisionType === 'BOTH') {
        if (!value || value.trim() === '') return 'Patient signature is required for patient supervision';
        if (value.length < 10) return 'Patient signature appears incomplete';
      }
      return null;
    }
  }
};

// Validation rules for monthly supervision workflow
const MONTHLY_SUPERVISION_VALIDATION_RULES: Record<string, Record<string, FieldValidationRule>> = {
  'review-due': {
    reviewComplete: {
      required: true,
      customValidator: (value) => {
        if (!value) return 'Review must be marked as complete before proceeding';
        return null;
      }
    },
    supervisionCount: {
      required: true,
      format: 'number',
      min: 0,
      customValidator: (value) => {
        if (!Number.isInteger(Number(value))) return 'Supervision count must be a whole number';
        if (Number(value) < 0) return 'Supervision count cannot be negative';
        return null;
      }
    },
    priorityList: {
      required: false,
      maxLength: 500
    }
  },
  'schedule-appointments': {
    appointmentsScheduled: {
      required: true,
      customValidator: (value) => {
        if (!value) return 'All required appointments must be scheduled';
        return null;
      }
    },
    conflictsResolved: {
      required: true,
      customValidator: (value) => {
        if (!value) return 'Any scheduling conflicts must be resolved';
        return null;
      }
    },
    confirmationsSent: {
      required: true,
      customValidator: (value) => {
        if (!value) return 'Appointment confirmations must be sent';
        return null;
      }
    }
  },
  'complete-forms': {
    formsCompleted: {
      required: true,
      customValidator: (value) => {
        if (!value) return 'All supervision forms must be completed';
        return null;
      }
    },
    formCount: {
      required: true,
      format: 'number',
      min: 0,
      dependencies: ['supervisionCount']
    }
  },
  'generate-reports': {
    reportGenerated: {
      required: true,
      customValidator: (value) => {
        if (!value) return 'Monthly report must be generated';
        return null;
      }
    },
    dataValidated: {
      required: true,
      customValidator: (value) => {
        if (!value) return 'Report data must be validated';
        return null;
      }
    }
  },
  'submit-summary': {
    summaryComplete: {
      required: true,
      customValidator: (value) => {
        if (!value) return 'Summary must be completed';
        return null;
      }
    },
    approvalObtained: {
      required: true,
      customValidator: (value) => {
        if (!value) return 'Supervisor approval must be obtained';
        return null;
      }
    }
  }
};

// Validation rules for discharge workflow
const DISCHARGE_VALIDATION_RULES: Record<string, Record<string, FieldValidationRule>> = {
  'initiation': {
    dischargeReason: {
      required: true,
      minLength: 10,
      maxLength: 500,
      customValidator: (value) => {
        if (!value || value.trim().length < 10) {
          return 'Discharge reason must be at least 10 characters';
        }
        return null;
      }
    },
    dischargeDate: {
      required: true,
      format: 'date',
      customValidator: (value) => {
        const date = new Date(value);
        const now = new Date();
        if (date > now) return 'Discharge date cannot be in the future';
        return null;
      }
    },
    initiatingParty: {
      required: true,
      customValidator: (value) => {
        const validParties = ['patient', 'family', 'physician', 'agency', 'insurance'];
        if (!validParties.includes(value)) {
          return 'Must select a valid initiating party';
        }
        return null;
      }
    }
  },
  'care-summary': {
    careSummaryComplete: {
      required: true,
      customValidator: (value) => {
        if (!value) return 'Care summary must be completed';
        return null;
      }
    },
    finalAssessment: {
      required: true,
      minLength: 50,
      maxLength: 2000,
      customValidator: (value) => {
        if (!value || value.trim().length < 50) {
          return 'Final assessment must be at least 50 characters';
        }
        return null;
      }
    },
    outcomeAchieved: {
      required: true,
      customValidator: (value) => {
        if (typeof value !== 'boolean') return 'Outcome achievement must be indicated';
        return null;
      }
    }
  },
  'notifications': {
    notificationsSent: {
      required: true,
      customValidator: (value) => {
        if (!value) return 'All required notifications must be sent';
        return null;
      }
    },
    physicianNotified: {
      required: true,
      customValidator: (value) => {
        if (!value) return 'Physician must be notified';
        return null;
      }
    },
    familyNotified: {
      required: true,
      customValidator: (value) => {
        if (!value) return 'Family/caregivers must be notified';
        return null;
      }
    }
  },
  'file-archive': {
    recordsArchived: {
      required: true,
      customValidator: (value) => {
        if (!value) return 'Records must be archived';
        return null;
      }
    },
    retentionSchedule: {
      required: true,
      customValidator: (value) => {
        const validSchedules = ['7-years', '10-years', 'permanent'];
        if (!validSchedules.includes(value)) {
          return 'Must select a valid retention schedule';
        }
        return null;
      }
    }
  },
  'confirmation': {
    dischargeConfirmed: {
      required: true,
      customValidator: (value) => {
        if (!value) return 'Discharge must be confirmed';
        return null;
      }
    },
    finalDocumentation: {
      required: true,
      customValidator: (value) => {
        if (!value) return 'Final documentation must be completed';
        return null;
      }
    }
  }
};

/**
 * Comprehensive workflow validation service
 */
export class WorkflowValidationService {
  
  /**
   * Validate a single field value against its rules
   */
  validateField(fieldName: string, value: any, rules: FieldValidationRule, allData?: any): ValidationError[] {
    const errors: ValidationError[] = [];

    // Required field validation
    if (rules.required && (value === null || value === undefined || value === '')) {
      errors.push({
        field: fieldName,
        message: `${this.formatFieldName(fieldName)} is required`,
        severity: 'error'
      });
      return errors; // Don't validate further if required field is empty
    }

    // Skip other validations if field is empty and not required
    if (!rules.required && (value === null || value === undefined || value === '')) {
      return errors;
    }

    // String length validation
    if (typeof value === 'string') {
      if (rules.minLength && value.length < rules.minLength) {
        errors.push({
          field: fieldName,
          message: `${this.formatFieldName(fieldName)} must be at least ${rules.minLength} characters`,
          severity: 'error'
        });
      }
      if (rules.maxLength && value.length > rules.maxLength) {
        errors.push({
          field: fieldName,
          message: `${this.formatFieldName(fieldName)} cannot exceed ${rules.maxLength} characters`,
          severity: 'error'
        });
      }
    }

    // Numeric validation
    if (typeof value === 'number' || (typeof value === 'string' && !isNaN(Number(value)))) {
      const numValue = Number(value);
      if (rules.min !== undefined && numValue < rules.min) {
        errors.push({
          field: fieldName,
          message: `${this.formatFieldName(fieldName)} must be at least ${rules.min}`,
          severity: 'error'
        });
      }
      if (rules.max !== undefined && numValue > rules.max) {
        errors.push({
          field: fieldName,
          message: `${this.formatFieldName(fieldName)} cannot exceed ${rules.max}`,
          severity: 'error'
        });
      }
    }

    // Pattern validation
    if (rules.pattern && typeof value === 'string' && !rules.pattern.test(value)) {
      errors.push({
        field: fieldName,
        message: `${this.formatFieldName(fieldName)} format is invalid`,
        severity: 'error'
      });
    }

    // Format validation
    if (rules.format && typeof value === 'string') {
      const pattern = VALIDATION_PATTERNS[rules.format];
      if (pattern && !pattern.test(value)) {
        errors.push({
          field: fieldName,
          message: `${this.formatFieldName(fieldName)} must be a valid ${rules.format}`,
          severity: 'error'
        });
      }
    }

    // Custom validation
    if (rules.customValidator) {
      const customError = rules.customValidator(value, allData);
      if (customError) {
        errors.push({
          field: fieldName,
          message: customError,
          severity: 'error'
        });
      }
    }

    return errors;
  }

  /**
   * Validate supervision form step data
   */
  validateSupervisionFormStep(step: SupervisionFormStep): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: ValidationError[] = [];
    const fieldErrors: Record<string, string> = {};

    // Get fields to validate based on step type
    const fieldsToValidate = this.getSupervisionFormFieldsForStep(step.stepType);
    
    for (const fieldName of fieldsToValidate) {
      const rules = SUPERVISION_FORM_VALIDATION_RULES[fieldName];
      if (rules) {
        const fieldValue = step.data[fieldName] || (step.sectionData as any)?.[fieldName];
        const validationErrors = this.validateField(fieldName, fieldValue, rules, { ...step.data, ...step.sectionData });
        
        errors.push(...validationErrors);
        
        // Store first error for each field
        const firstError = validationErrors.find(e => e.severity === 'error');
        if (firstError) {
          fieldErrors[fieldName] = firstError.message;
        }
      }
    }

    // Cross-field validation for supervision form
    this.addSupervisionFormCrossFieldValidation(step, errors, warnings);

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      fieldErrors
    };
  }

  /**
   * Validate monthly supervision step data
   */
  validateMonthlySupervisionStep(step: MonthlySupervisionStep): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: ValidationError[] = [];
    const fieldErrors: Record<string, string> = {};

    const stepRules = MONTHLY_SUPERVISION_VALIDATION_RULES[step.stepType];
    if (stepRules) {
      for (const [fieldName, rules] of Object.entries(stepRules)) {
        const fieldValue = step.data[fieldName];
        const validationErrors = this.validateField(fieldName, fieldValue, rules, step.data);
        
        errors.push(...validationErrors);
        
        const firstError = validationErrors.find(e => e.severity === 'error');
        if (firstError) {
          fieldErrors[fieldName] = firstError.message;
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      fieldErrors
    };
  }

  /**
   * Validate discharge step data
   */
  validateDischargeStep(step: DischargeStep): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: ValidationError[] = [];
    const fieldErrors: Record<string, string> = {};

    const stepRules = DISCHARGE_VALIDATION_RULES[step.stepType];
    if (stepRules) {
      for (const [fieldName, rules] of Object.entries(stepRules)) {
        const fieldValue = step.data[fieldName];
        const validationErrors = this.validateField(fieldName, fieldValue, rules, step.data);
        
        errors.push(...validationErrors);
        
        const firstError = validationErrors.find(e => e.severity === 'error');
        if (firstError) {
          fieldErrors[fieldName] = firstError.message;
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      fieldErrors
    };
  }

  /**
   * Validate any workflow step
   */
  validateWorkflowStep(workflow: AnyWorkflowInstance, stepIndex: number): ValidationResult {
    const step = workflow.steps[stepIndex];
    if (!step) {
      return {
        isValid: false,
        errors: [{ field: 'step', message: 'Step not found', severity: 'error' }],
        warnings: [],
        fieldErrors: { step: 'Step not found' }
      };
    }

    switch (workflow.type) {
      case 'supervision-form':
        return this.validateSupervisionFormStep(step as SupervisionFormStep);
      case 'monthly-supervision':
        return this.validateMonthlySupervisionStep(step as MonthlySupervisionStep);
      case 'discharge-process':
        return this.validateDischargeStep(step as DischargeStep);
      default:
        return {
          isValid: true,
          errors: [],
          warnings: [],
          fieldErrors: {}
        };
    }
  }

  /**
   * Validate entire workflow
   */
  validateEntireWorkflow(workflow: AnyWorkflowInstance): ValidationResult {
    const allErrors: ValidationError[] = [];
    const allWarnings: ValidationError[] = [];
    const allFieldErrors: Record<string, string> = {};

    for (let i = 0; i < workflow.steps.length; i++) {
      const stepResult = this.validateWorkflowStep(workflow, i);
      allErrors.push(...stepResult.errors);
      allWarnings.push(...stepResult.warnings);
      Object.assign(allFieldErrors, stepResult.fieldErrors);
    }

    return {
      isValid: allErrors.length === 0,
      errors: allErrors,
      warnings: allWarnings,
      fieldErrors: allFieldErrors
    };
  }

  /**
   * Get fields to validate for a specific supervision form step
   */
  private getSupervisionFormFieldsForStep(stepType: SupervisionFormStep['stepType']): string[] {
    switch (stepType) {
      case 'basic-info':
        return ['supervisionDate', 'supervisionType', 'employeeId', 'patientId', 'aideName', 'clientNdocNumber'];
      case 'section-1':
        return ['aideAppropratelyAttired', 'introducesSlefToPatient', 'clearlyIdentifiesPatient', 
                'reliableAndPunctual', 'goodRelationshipWithPatient', 'ensuresPatientRights'];
      case 'section-2':
        return ['usesStandardPrecautions', 'followsSpecificPrecautions'];
      case 'section-3':
        return ['followsCarePlan', 'personalCareSkinBreakdown', 'dressing', 'mealPreparationFollowsDiet',
                'transfers', 'exercises', 'catheterCare', 'ostomyCare', 'vitalSignsWeight', 
                'medicationReminding', 'lightHousekeeping', 'errandsShoppig', 'specialTasks'];
      case 'section-4':
        return ['documentationNoAbbreviations', 'contactsCMChangesInCondition', 
                'contactsCMPatientComplaints', 'contactsCMPatientSafety'];
      case 'section-5':
        return ['medicationNonCompliance', 'commentsRecommendationsPage1'];
      case 'page-2':
        return ['involvesPatientInPlanning', 'allowsPatientControl', 'recognizesHearingSightLoss', 
                'slowsPaceForElderly', 'commentsRecommendationsPage2'];
      case 'validation':
        return ['additionalValidationRequired', 'performanceAchieved'];
      case 'signatures':
        return ['employeeSignature', 'evaluatorSignature', 'patientSignature'];
      case 'review':
        return []; // Review step validates all previous data
      default:
        return [];
    }
  }

  /**
   * Add cross-field validation for supervision form
   */
  private addSupervisionFormCrossFieldValidation(
    step: SupervisionFormStep, 
    errors: ValidationError[], 
    warnings: ValidationError[]
  ): void {
    const allData = { ...step.data, ...step.sectionData } as any;

    // Patient signature validation based on supervision type
    if (step.stepType === 'signatures') {
      if ((allData.supervisionType === 'PATIENT' || allData.supervisionType === 'BOTH') && 
          !allData.patientSignature) {
        errors.push({
          field: 'patientSignature',
          message: 'Patient signature is required for patient supervision',
          severity: 'error'
        });
      }
    }

    // Warn if special tasks is long but other treatment fields are N/A
    if (step.stepType === 'section-3' && allData.specialTasks && allData.specialTasks.length > 100) {
      const treatmentFields = ['followsCarePlan', 'personalCareSkinBreakdown', 'dressing', 
                              'mealPreparationFollowsDiet', 'transfers', 'exercises'];
      const naCount = treatmentFields.filter(field => allData[field] === 'N/A').length;
      
      if (naCount > treatmentFields.length / 2) {
        warnings.push({
          field: 'specialTasks',
          message: 'Consider if some N/A treatment tasks should be addressed in special tasks',
          severity: 'warning'
        });
      }
    }
  }

  /**
   * Format field name for display in error messages
   */
  private formatFieldName(fieldName: string): string {
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Get validation rules for a specific field
   */
  getFieldValidationRules(workflowType: WorkflowType, stepType: string, fieldName: string): FieldValidationRule | null {
    switch (workflowType) {
      case 'supervision-form':
        return SUPERVISION_FORM_VALIDATION_RULES[fieldName] || null;
      case 'monthly-supervision':
        return MONTHLY_SUPERVISION_VALIDATION_RULES[stepType]?.[fieldName] || null;
      case 'discharge-process':
        return DISCHARGE_VALIDATION_RULES[stepType]?.[fieldName] || null;
      default:
        return null;
    }
  }

  /**
   * Real-time field validation for forms
   */
  validateFieldRealTime(
    workflowType: WorkflowType, 
    stepType: string, 
    fieldName: string, 
    value: any, 
    allData?: any
  ): ValidationError | null {
    const rules = this.getFieldValidationRules(workflowType, stepType, fieldName);
    if (!rules) return null;

    const errors = this.validateField(fieldName, value, rules, allData);
    return errors.length > 0 ? errors[0] : null;
  }
}

// Export singleton instance
export const workflowValidationService = new WorkflowValidationService(); 